/**
 * Meta Pixel + Conversions API (CAPI) — consent-gated event layer.
 *
 * GDPR: the Meta Pixel (fbevents.js) is loaded and fbq() is called ONLY
 * after the visitor grants explicit consent for marketing cookies.
 * The same gate applies to server-side CAPI forwarding — without consent
 * no tracking data leaves the browser at all.
 *
 * Initialization (single source of truth):
 *   - App.js boot:        initFromStoredConsent()  — respects saved consent
 *   - CookieBanner:       initializeMetaPixel()    — when marketing is accepted
 *
 * Events: each helper fires
 *   1. Browser-side fbq() call (Meta Pixel)
 *   2. Server-side CAPI request via the backend /api/meta-capi/* endpoint
 * Both share the same event_id so Meta deduplicates them in Events Manager.
 *
 * Design goals:
 *  - Never break UX: every call is wrapped and no-ops without consent/fbq.
 *  - Stateless events: event_id is generated per call via crypto.randomUUID().
 *  - No secrets in the browser: the CAPI token lives only in the backend.
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
const API_URL = process.env.REACT_APP_BACKEND_URL;

if (!PIXEL_ID && process.env.NODE_ENV !== 'test') {
  // eslint-disable-next-line no-console
  console.warn('[metaPixel] REACT_APP_META_PIXEL_ID is not set. Meta Pixel will not be initialized.');
}

let _initialized = false;
// Cached hashed Advanced Matching data — sent with every CAPI event and used
// to re-init the browser Pixel when the user logs in.
let _userMatchData = null;

/**
 * Compute lowercase hex SHA-256 of an input string.
 * Uses the Web Crypto API (SubtleCrypto). Returns empty string on failure.
 */
const _sha256 = async (input) => {
  if (!input || typeof input !== 'string') return '';
  const value = input.trim().toLowerCase();
  if (!value) return '';
  try {
    if (typeof window === 'undefined' || !window.crypto?.subtle) return '';
    const bytes = new TextEncoder().encode(value);
    const hashBuf = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hashBuf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return '';
  }
};

/**
 * Read Meta first-party cookies (_fbp, _fbc) for CAPI attribution.
 */
const _readFbCookies = () => {
  if (typeof document === 'undefined') return {};
  const out = {};
  document.cookie.split(';').forEach((part) => {
    const [k, v] = part.trim().split('=');
    if (k === '_fbp' && v) out.fbp = v;
    if (k === '_fbc' && v) out.fbc = v;
  });
  return out;
};

/**
 * Set / update Advanced Matching data for future events.
 *
 * Accepts *plain* PII (email, phone, first/last name, city, state, zip, country,
 * user id). We hash everything client-side with SHA-256 before storing or sending.
 * Re-initialises the browser Pixel so subsequent PageView / events include the
 * matching hashes (Meta uses these to attribute more conversions).
 *
 * Call this on:
 *   - login success (email + name + user id)
 *   - registration success
 *   - checkout submit (adds phone + address fields)
 */
export const setAdvancedMatching = async (userInfo = {}) => {
  if (typeof window === 'undefined') return;

  // Normalise phone: strip leading + and any non-digits (Meta wants digits only)
  const phone = (userInfo.phone || '').replace(/[^\d]/g, '');
  const country = (userInfo.country || '').trim().toLowerCase();
  const [firstName, ...rest] = (userInfo.name || '').trim().split(/\s+/);
  const lastName = rest.join(' ');

  const [em, ph, fn, ln, ct, st, zp, cn, ext] = await Promise.all([
    _sha256(userInfo.email),
    phone ? _sha256(phone) : '',
    firstName ? _sha256(firstName) : '',
    lastName ? _sha256(lastName) : '',
    _sha256(userInfo.city),
    _sha256(userInfo.state),
    _sha256(userInfo.zip),
    country ? _sha256(country) : '',
    userInfo.userId ? _sha256(String(userInfo.userId)) : '',
  ]);

  const matchData = {};
  if (em) matchData.em = em;
  if (ph) matchData.ph = ph;
  if (fn) matchData.fn = fn;
  if (ln) matchData.ln = ln;
  if (ct) matchData.ct = ct;
  if (st) matchData.st = st;
  if (zp) matchData.zp = zp;
  if (cn) matchData.country = cn;
  if (ext) matchData.external_id = ext;

  const fbCookies = _readFbCookies();
  if (fbCookies.fbp) matchData.fbp = fbCookies.fbp;
  if (fbCookies.fbc) matchData.fbc = fbCookies.fbc;

  _userMatchData = matchData;

  // If the Pixel is already initialised, re-init with the new matching data
  // so subsequent browser events include it.
  if (_initialized && PIXEL_ID && typeof window.fbq === 'function') {
    try {
      // Meta Pixel accepts advanced matching object as 3rd param of init
      window.fbq('init', PIXEL_ID, matchData);
    } catch {
      /* never break UX */
    }
  }
};

/**
 * Clear Advanced Matching data (e.g. on logout).
 */
export const clearAdvancedMatching = () => {
  _userMatchData = null;
  if (_initialized && PIXEL_ID && typeof window.fbq === 'function') {
    try {
      window.fbq('init', PIXEL_ID);
    } catch {
      /* silent */
    }
  }
};

/** Returns true only after the Pixel script has been loaded and fbq('init') called. */
export const isMetaPixelInitialized = () => _initialized;

/**
 * Load fbevents.js and call fbq('init', PIXEL_ID) + fbq('track', 'PageView').
 * Safe to call multiple times — subsequent calls are no-ops.
 * Must be called only when marketing cookie consent has been granted.
 */
export const initializeMetaPixel = () => {
  if (_initialized || typeof window === 'undefined') return;
  if (!PIXEL_ID) {
    // eslint-disable-next-line no-console
    console.warn('[metaPixel] Cannot initialize: REACT_APP_META_PIXEL_ID is not set.');
    return;
  }

  // Inject the Meta Pixel bootstrap snippet
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  // Advanced Matching data (if already set, e.g. logged-in returning visitor)
  if (_userMatchData && Object.keys(_userMatchData).length > 0) {
    window.fbq('init', PIXEL_ID, _userMatchData);
  } else {
    window.fbq('init', PIXEL_ID);
  }
  _initialized = true;

  // Fire PageView on BOTH browser Pixel + CAPI with a shared eventID so Meta
  // deduplicates them in Events Manager. Only fire once here.
  const eventId = newEventId();
  window.fbq('trackSingle', PIXEL_ID, 'PageView', {}, { eventID: eventId });
  sendCapi('PageView', {
    event_id: eventId,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
  });
};

/**
 * Safe wrapper around window.fbq().
 * Silently does nothing if the Pixel has not been initialized (no consent yet).
 * A failed analytics call must never propagate into the React component tree.
 */
export const trackMetaEvent = (...args) => {
  if (!_initialized || typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq(...args);
  } catch {
    /* swallow — analytics must never break UX */
  }
};

/**
 * Called once on app boot (e.g. inside App.js useEffect).
 * Reads the persisted cookie_consent value from localStorage and initializes
 * the Pixel only when the visitor has already given marketing consent.
 */
export const initFromStoredConsent = () => {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem('cookie_consent');
    console.log('[metaPixel][initFromStoredConsent] raw=', raw);
    if (!raw) return;
    const prefs = JSON.parse(raw);
    console.log('[metaPixel][initFromStoredConsent] parsed=', prefs, 'marketing=', !!prefs.marketing);
    if (prefs && prefs.marketing === true) {
      initializeMetaPixel();
    }
  } catch (err) {
    console.log('[metaPixel][initFromStoredConsent] invalid localStorage', err);
  }
};

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Consent-gated fbq wrapper used by all event helpers below. */
const fbq = (...args) => trackMetaEvent(...args);

/** Generate a unique event ID for Pixel ↔ CAPI deduplication. */
const newEventId = () => {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

/**
 * Forward the event to the backend CAPI endpoint.
 * GDPR: no-ops until marketing consent has been granted (_initialized).
 * Failures are silently swallowed so the UX is never affected.
 */
/**
 * Server-side CAPI fire is decoupled from the browser Pixel:
 * - It still requires marketing consent (checked via _hasMarketingConsent()).
 * - It does NOT require REACT_APP_META_PIXEL_ID to be present, so CAPI keeps
 *   working even if the frontend pixel snippet cannot boot (ad-blockers,
 *   missing env, iOS ATT, etc.). The Meta account uses METAPIXEL_ID server-side.
 */
const _hasMarketingConsent = () => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem('cookie_consent');
    if (!raw) return false;
    const prefs = JSON.parse(raw);
    return prefs && prefs.marketing === true;
  } catch {
    return false;
  }
};

const sendCapi = async (eventName, payload) => {
  // Fire CAPI on marketing consent alone. The browser Pixel init state is
  // irrelevant server-side — Meta's server matches events by our account.
  if (!API_URL || !_hasMarketingConsent()) return;
  try {
    // Include Advanced Matching + fresh _fbp / _fbc cookies on every event
    const fbCookies = _readFbCookies();
    const userData = { ..._userMatchData };
    if (fbCookies.fbp) userData.fbp = fbCookies.fbp;
    if (fbCookies.fbc) userData.fbc = fbCookies.fbc;

    const body = { event_name: eventName, ...payload };
    if (Object.keys(userData).length > 0) {
      body.user_data = userData;
    }

    await fetch(`${API_URL}/api/meta-capi/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
  } catch {
    /* swallow: CAPI failures must not affect the user */
  }
};

// ─── event helpers ────────────────────────────────────────────────────────────

/**
 * Build a content_ids + contents array from a product object.
 * `catalogId` is deliberately the SAME id the product already carries
 * (product.id === the Mongo _id string === the Meta Catalog retailer_id/`id`
 * field set in utils/meta_catalog.py transform_product_for_meta). No separate
 * or generated id is ever used for Pixel/CAPI events.
 */
const productContents = (product, quantity = 1) => {
  const catalogId = String(product?.id ?? '');
  const price = Number(product?.price ?? 0);
  return {
    content_ids: [catalogId],
    content_type: 'product',
    content_name: product?.name ?? '',
    content_category: product?.category ?? '',
    value: price * quantity,
    currency: 'EUR',
    contents: [{ id: catalogId, quantity, item_price: price }],
  };
};

// ─── 10 events ────────────────────────────────────────────────────────────────

/**
 * 1. ViewContent — fired ONLY on the product detail page (/product/{id}),
 * never on listing/category/collection/search/filter pages.
 */
export const pixelViewContent = (product) => {
  if (!product || !product.id) return;
  const eventId = newEventId();
  const params = productContents(product, 1);
  fbq('track', 'ViewContent', params, { eventID: eventId });
  sendCapi('ViewContent', {
    event_id: eventId,
    product_id: params.content_ids[0],
    content_ids: params.content_ids,
    contents: params.contents,
    value: params.value,
    content_name: params.content_name,
    category: params.content_category,
    currency: 'EUR',
  });
};

/**
 * 2. Search — fired when the user submits a search query on the Products page.
 */
export const pixelSearch = (query) => {
  if (!query) return;
  const eventId = newEventId();
  fbq('track', 'Search', { search_string: query }, { eventID: eventId });
  sendCapi('Search', { event_id: eventId, search_string: query });
};

/**
 * 3. AddToWishlist — custom event fired when the wishlist button is clicked.
 */
export const pixelAddToWishlist = (product) => {
  if (!product || !product.id) return;
  const eventId = newEventId();
  const params = productContents(product, 1);
  fbq('track', 'AddToWishlist', params, { eventID: eventId });
  sendCapi('AddToWishlist', {
    event_id: eventId,
    product_id: params.content_ids[0],
    content_ids: params.content_ids,
    contents: params.contents,
    value: params.value,
    content_name: params.content_name,
    category: params.content_category,
    currency: 'EUR',
  });
};

/**
 * 4. AddToCart — fired when a product is added to the shopping cart.
 */
export const pixelAddToCart = (product, quantity = 1) => {
  if (!product || !product.id) return;
  const eventId = newEventId();
  const params = productContents(product, quantity);
  fbq('track', 'AddToCart', params, { eventID: eventId });
  sendCapi('AddToCart', {
    event_id: eventId,
    product_id: params.content_ids[0],
    content_ids: params.content_ids,
    contents: params.contents,
    quantity,
    value: params.value,
    content_name: params.content_name,
    category: params.content_category,
    currency: 'EUR',
  });
};

/**
 * 5. InitiateCheckout — fired when the user lands on the checkout page.
 */
export const pixelInitiateCheckout = (cartItems = []) => {
  if (!cartItems.length) return;
  const eventId = newEventId();
  const contents = cartItems.map((i) => ({ id: String(i.id), quantity: Number(i.quantity ?? 1), item_price: Number(i.price ?? 0) }));
  const contentIds = contents.map((c) => c.id);
  const value = contents.reduce((s, c) => s + c.item_price * c.quantity, 0);
  fbq(
    'track',
    'InitiateCheckout',
    { content_ids: contentIds, content_type: 'product', contents, num_items: cartItems.length, value, currency: 'EUR' },
    { eventID: eventId },
  );
  sendCapi('InitiateCheckout', {
    event_id: eventId,
    content_ids: contentIds,
    contents,
    num_items: cartItems.length,
    value,
    currency: 'EUR',
  });
};

/**
 * 6. Purchase — fired exactly once per successfully confirmed order.
 * eventID is deterministic (`purchase_${orderId}`) rather than random so
 * that repeated calls for the SAME order (page refresh, polling retries,
 * etc.) always dedupe against each other AND against the CAPI copy of the
 * same event - Meta collapses duplicates that share an event_id.
 */
export const pixelPurchase = ({ orderId, value, cartItems = [] }) => {
  if (!orderId) return;
  const eventId = `purchase_${orderId}`;
  const contents = cartItems.map((i) => ({ id: String(i.id), quantity: Number(i.quantity ?? 1), item_price: Number(i.price ?? 0) }));
  const contentIds = contents.map((c) => c.id);
  fbq(
    'track',
    'Purchase',
    { content_ids: contentIds, content_type: 'product', contents, num_items: cartItems.length, value: Number(value), currency: 'EUR' },
    { eventID: eventId },
  );
  sendCapi('Purchase', {
    event_id: eventId,
    order_id: String(orderId),
    content_ids: contentIds,
    contents,
    value: Number(value),
    num_items: cartItems.length,
    currency: 'EUR',
  });
};

/**
 * 7. Lead — fired when a user subscribes to the newsletter during registration.
 */
export const pixelLead = () => {
  const eventId = newEventId();
  fbq('track', 'Lead', {}, { eventID: eventId });
  sendCapi('Lead', { event_id: eventId });
};

/**
 * 8. CompleteRegistration — fired after a successful user account registration.
 */
export const pixelCompleteRegistration = () => {
  const eventId = newEventId();
  fbq('track', 'CompleteRegistration', { status: true }, { eventID: eventId });
  sendCapi('CompleteRegistration', { event_id: eventId });
};

/**
 * 9. ViewCategory — custom event fired when the user selects a product category.
 */
export const pixelViewCategory = (category) => {
  if (!category || category === 'all') return;
  const eventId = newEventId();
  fbq('trackCustom', 'ViewCategory', { content_category: category }, { eventID: eventId });
  sendCapi('ViewCategory', { event_id: eventId, category });
};

/**
 * 10. DiscountApplied — custom event fired when a discount code is successfully applied.
 */
export const pixelDiscountApplied = ({ code, discountAmount, cartTotal }) => {
  const eventId = newEventId();
  fbq(
    'trackCustom',
    'DiscountApplied',
    { discount_code: code, discount_amount: discountAmount, cart_total: cartTotal, currency: 'EUR' },
    { eventID: eventId },
  );
  sendCapi('DiscountApplied', { event_id: eventId, discount_code: code, discount_amount: discountAmount, cart_total: cartTotal });
};

const metaPixel = {
  isMetaPixelInitialized,
  initializeMetaPixel,
  trackMetaEvent,
  initFromStoredConsent,
  setAdvancedMatching,
  clearAdvancedMatching,
  pixelViewContent,
  pixelSearch,
  pixelAddToWishlist,
  pixelAddToCart,
  pixelInitiateCheckout,
  pixelPurchase,
  pixelLead,
  pixelCompleteRegistration,
  pixelViewCategory,
  pixelDiscountApplied,
};

export default metaPixel;
